import moment from 'moment';
import { createContext, useContext, useEffect, useState } from 'react';
import useReferralActions from '../hooks/APIHooks/users/useReferralActions';
import { AssociateReferral, Referral } from '../types/referral';
import { User } from '../types/users';
import { useAuthContext } from './AuthProvider';

// datatype of the context declared here
interface IReferralHistoryContext {
  isReferralHistoryLoading: boolean;
  isReferralHistoryFetching: boolean;
  referralHistoryType: string;
  referralHistoryList: Array<Referral>;
  associateReferralList: Array<AssociateReferral>;
  referral: Referral | AssociateReferral | null;
  showReferralDetailsModal: boolean;
  setReferralHistoryType: React.Dispatch<React.SetStateAction<string>>;
  setReferral: React.Dispatch<
    React.SetStateAction<Referral | AssociateReferral | null>
  >;
  setShowReferralDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
}

// creating the context here
const ReferralHistoryContext = createContext<IReferralHistoryContext | null>(
  null
);

const ReferralHistoryProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // integration of react hooks here
  const [referralHistoryType, setReferralHistoryType] = useState<string>('to');
  const [referralHistoryList, setReferralHistoryList] = useState<
    Array<Referral>
  >([]);
  const [associateReferralList, setAssociateReferralList] = useState<
    Array<AssociateReferral>
  >([]);
  const [referral, setReferral] = useState<Referral | AssociateReferral | null>(
    null
  );
  const [showReferralDetailsModal, setShowReferralDetailsModal] =
    useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const {
    getReferralsListHandler,
    getReferralsByAssociateId,
    getReferredByAssociateId,
  } = useReferralActions(
    (currentUser as User)?.role_name === 'Admin'
      ? 'referralList'
      : referralHistoryType === 'to'
      ? 'referralsById'
      : 'referredById',
    (currentUser as User)?.role_name === 'Admin' ? undefined : currentUser?.id
  );

  // setting the referral history data into local state here
  useEffect(() => {
    if ((currentUser as User)?.role_name === 'Admin') {
      if (getReferralsListHandler.data?.is_success) {
        setReferralHistoryList(
          getReferralsListHandler.data.data.map((referral) => ({
            ...referral,
            referred_at: moment(referral.referred_at).format('DD-MM-YYYY'),
            responded_at: referral.responded_at
              ? moment(referral.responded_at).format('DD-MM-YYYY')
              : 'N/A',
            status: referral.is_accepted ? 'Accepted' : 'Pending',
          }))
        );
      }
    } else {
      if (
        referralHistoryType === 'to' &&
        getReferralsByAssociateId.data?.is_success
      ) {
        setAssociateReferralList(
          getReferralsByAssociateId.data.data.map((referral) => ({
            ...referral,
            referred_at: moment(referral.referred_at).format('DD-MM-YYYY'),
            responded_at: referral.responded_at
              ? moment(referral.responded_at).format('DD-MM-YYYY')
              : 'N/A',
            status: referral.is_accepted ? 'Accepted' : 'Pending',
          }))
        );
      }

      if (
        referralHistoryType === 'by' &&
        getReferredByAssociateId.data?.is_success
      ) {
        setAssociateReferralList(
          getReferredByAssociateId.data.data.map((referral) => ({
            ...referral,
            referred_at: moment(referral.referred_at).format('DD-MM-YYYY'),
            responded_at: referral.responded_at
              ? moment(referral.responded_at).format('DD-MM-YYYY')
              : 'N/A',
            status: referral.is_accepted ? 'Accepted' : 'Pending',
          }))
        );
      }
    }
  }, [
    currentUser,
    referralHistoryType,
    getReferralsListHandler.data,
    getReferralsByAssociateId.data,
    getReferredByAssociateId.data,
  ]);

  useEffect(() => {
    setReferralHistoryList([]);
    setAssociateReferralList([]);

    if (referralHistoryType === 'to') {
      getReferralsByAssociateId.refetch();
    } else {
      getReferredByAssociateId.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralHistoryType]);

  console.log('REFERRAL HISTORY: ', referralHistoryList, associateReferralList);

  // rendering the context provider here
  return (
    <ReferralHistoryContext.Provider
      value={{
        isReferralHistoryLoading: getReferralsListHandler.isLoading,
        isReferralHistoryFetching:
          referralHistoryType === 'to'
            ? getReferralsByAssociateId.isFetching
            : getReferredByAssociateId.isFetching,
        referralHistoryType,
        referralHistoryList,
        associateReferralList,
        referral,
        showReferralDetailsModal,
        setReferralHistoryType,
        setReferral,
        setShowReferralDetailsModal,
      }}
    >
      {children}
    </ReferralHistoryContext.Provider>
  );
};

export default ReferralHistoryProvider;

// custom hook to access referral context declared here
export const useReferralHistoryContext = () => {
  const context = useContext(ReferralHistoryContext);

  if (!context) {
    throw new Error(
      'useReferralHistoryContext must be within ReferralHistoryProvider!'
    );
  }

  return context;
};
